import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Help as HelpIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SuperUserIcon,
  Palette as PaletteIcon,
  DragIndicator as DragIcon,
  Search as SearchIcon,
  Keyboard as KeyboardIcon,
  ExpandMore as ExpandMoreIcon,
  Lightbulb as TipIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const HelpPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const KeyboardShortcut: React.FC<{ keys: string; description: string }> = ({ keys, description }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <Box sx={{ minWidth: 100 }}>
        {keys.split('+').map((key, index) => (
          <React.Fragment key={key}>
            <Chip size="small" label={key} sx={{ mx: 0.25 }} />
            {index < keys.split('+').length - 1 && <span> + </span>}
          </React.Fragment>
        ))}
      </Box>
      <Typography variant="body2" sx={{ ml: 2 }}>
        {description}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Paper sx={{ mt: 2, mb: 4 }}>
          {/* Header */}
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/dashboard')}
                sx={{ mr: 2 }}
              >
                Back to Dashboard
              </Button>
              <HelpIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                Documentation & Help
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Comprehensive guide to using the ThinkBeyond Dashboard
            </Typography>
          </Box>

          {/* Navigation Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="help tabs">
              <Tab label="Getting Started" />
              <Tab label="Dashboard Guide" />
              <Tab label="Browser Setup" />
              <Tab label="User Management" />
              <Tab label="Keyboard Shortcuts" />
              <Tab label="Troubleshooting" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h5" gutterBottom>
              Getting Started
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <DashboardIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Dashboard Basics</Typography>
                    </Box>
                    <List>
                      <ListItem>
                        <ListItemText 
                          primary="Tiles & Groups" 
                          secondary="Organize your links into groups and tiles for easy access"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Drag & Drop" 
                          secondary="Reorder tiles and groups by dragging them around"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Quick Search" 
                          secondary="Use Ctrl+K or the search bar to find tiles quickly"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PaletteIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Customization</Typography>
                    </Box>
                    <List>
                      <ListItem>
                        <ListItemText 
                          primary="Themes" 
                          secondary="Choose from 20+ predefined themes or create your own"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Icons" 
                          secondary="Select from hundreds of Material-UI icons for tiles and groups"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Colors" 
                          secondary="Customize colors for individual tiles and groups"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">User Roles Explained</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ mr: 1, color: 'info.main' }} />
                        <Typography variant="subtitle1">User</Typography>
                      </Box>
                      <Typography variant="body2">
                        • Full dashboard customization
                        • Theme and layout control
                        • Personal settings management
                        • Cannot manage other users
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AdminIcon sx={{ mr: 1, color: 'warning.main' }} />
                        <Typography variant="subtitle1">Admin</Typography>
                      </Box>
                      <Typography variant="body2">
                        • All user capabilities
                        • Create and manage regular users
                        • Manage other admin accounts
                        • Change passwords for managed users
                        • Delete user accounts
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SuperUserIcon sx={{ mr: 1, color: 'error.main' }} />
                        <Typography variant="subtitle1">Superuser</Typography>
                      </Box>
                      <Typography variant="body2">
                        • All admin capabilities
                        • Create admin accounts
                        • Manage system-wide settings
                        • Edit default dashboard templates
                        • Full user management access
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h5" gutterBottom>
              Dashboard Guide
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Managing Tiles
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <TipIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Creating Tiles" 
                      secondary="Click 'Add Tile' in edit mode to create new shortcuts"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <DragIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Reordering" 
                      secondary="Drag tiles to reorder them within groups or move between groups"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <SettingsIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Editing" 
                      secondary="Right-click on tiles or use the three-dot menu to edit properties"
                    />
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Managing Groups
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <TipIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Creating Groups" 
                      secondary="Use 'Add Group' to organize tiles into categories"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <PaletteIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Customizing" 
                      secondary="Set custom icons, colors, and names for each group"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <DragIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Reordering" 
                      secondary="Drag group headers to change their order on the dashboard"
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Theme Customization
            </Typography>
            <Typography variant="body1" paragraph>
              The dashboard includes over 20 predefined themes including corporate themes, popular app themes, 
              and custom color schemes. You can also create your own theme by selecting custom colors and 
              saving them as a personal theme.
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Popular Themes:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {['TrueDot', 'Material Design', 'GitHub Dark', 'Spotify', 'Netflix', 'Corporate Blue'].map((theme) => (
                  <Chip key={theme} label={theme} size="small" />
                ))}
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h5" gutterBottom>
              Browser Setup
            </Typography>
            <Typography variant="body1" paragraph>
              Set up your browser to use this dashboard as your home page or new tab page for quick access.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJDNi40OCAyIDIgNi40OCAyIDEyUzYuNDggMjIgMTIgMjJTMjIgMTcuNTIgMjIgMTJTMTcuNTIgMiAxMiAyWk0xNi4zNSAxNy43NEM5Ljc5IDE3Ljc0IDQuMDMgMTMuNzggMy40OCAxMC42MkM0LjIzIDEwLjc5IDUuMDEgMTAuODggNS44MSAxMC44OEM3LjM0IDEwLjg4IDguNzQgMTAuNjEgMTAuMDEgMTAuMTNDMTAuNzggMTAuNjcgMTEuNzIgMTEgMTIuNzYgMTFDMTQuNzQgMTEgMTYuNDQgMTAuMDMgMTcuMzUgOC41M0MxNy43NCA5Ljg5IDE3Ljc0IDExLjM1IDE3LjM1IDEyLjcxQzE3LjM1IDE0Ljc5IDE2LjM1IDE3Ljc0IDE2LjM1IDE3Ljc0WiIgZmlsbD0iIzQyODVGNCIvPgo8L3N2Zz4K" alt="Chrome" style={{ width: 20, height: 20 }} />
                      Google Chrome
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>Method 1: New Tab Redirect Extension</strong>
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="1. Click the three vertical dots (⋮) in the top right corner" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="2. Select 'Extensions' → 'Visit Chrome Web Store'" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="3. Search for 'New Tab Redirect'" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="4. Click 'Add to Chrome' → 'Add Extension'" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="5. At welcome screen, click 'Options'" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="6. Enter your dashboard URL and click 'Save'" />
                      </ListItem>
                    </List>
                    <Paper sx={{ p: 1, mt: 1, bgcolor: 'grey.100' }}>
                      <Typography variant="caption">
                        Example URL: https://my-dashboard.company.com
                      </Typography>
                    </Paper>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJMMTMuMDkgOC4yNkwyMCA5TDEzLjA5IDE1Ljc0TDEyIDIyTDEwLjkxIDE1Ljc0TDQgOUwxMC45MSA4LjI2TDEyIDJaIiBmaWxsPSIjMDA1RkVGIi8+Cjwvc3ZnPgo=" alt="Safari" style={{ width: 20, height: 20 }} />
                      Safari (macOS)
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="1. Open Safari → Settings (or Preferences)" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="2. Select 'General' tab" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="3. Modify these settings:" />
                      </ListItem>
                      <List sx={{ pl: 2 }}>
                        <ListItem>
                          <ListItemText 
                            primary="• Homepage: Enter your dashboard URL"
                            secondary="• New windows open with: Homepage"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="• New tabs open with: Homepage" />
                        </ListItem>
                      </List>
                    </List>
                    <Paper sx={{ p: 1, mt: 1, bgcolor: 'grey.100' }}>
                      <Typography variant="caption">
                        Example URL: https://my-dashboard.company.com
                      </Typography>
                    </Paper>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJMMTMuMDkgOC4yNkwyMCA5TDEzLjA5IDE1Ljc0TDEyIDIyTDEwLjkxIDE1Ljc0TDQgOUwxMC45MSA4LjI2TDEyIDJaIiBmaWxsPSIjRkY1NzMzIi8+Cjwvc3ZnPgo=" alt="Firefox" style={{ width: 20, height: 20 }} />
                      Mozilla Firefox
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="1. Open Settings menu → 'Home'" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="2. Under 'Homepage and new windows':" />
                      </ListItem>
                      <List sx={{ pl: 2 }}>
                        <ListItem>
                          <ListItemText primary="• Change dropdown to 'Custom URLs...'" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="• Enter your dashboard URL" />
                        </ListItem>
                      </List>
                      <ListItem>
                        <ListItemText primary="3. Under 'New Tabs':" />
                      </ListItem>
                      <List sx={{ pl: 2 }}>
                        <ListItem>
                          <ListItemText primary="• Change dropdown to 'Custom URLs...'" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="• Enter your dashboard URL" />
                        </ListItem>
                      </List>
                    </List>
                    <Paper sx={{ p: 1, mt: 1, bgcolor: 'grey.100' }}>
                      <Typography variant="caption">
                        Example URL: https://my-dashboard.company.com
                      </Typography>
                    </Paper>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Chip 
                icon={<TipIcon />} 
                label="Pro Tip: Bookmark your dashboard for quick access from any device!" 
                color="primary" 
                variant="outlined" 
              />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Typography variant="h5" gutterBottom>
              User Management
            </Typography>

            {user?.role === 'user' ? (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PersonIcon sx={{ mr: 1, color: 'info.main' }} />
                        <Typography variant="h6">User Capabilities</Typography>
                      </Box>
                      <List>
                        <ListItem>
                          <ListItemText 
                            primary="Dashboard Customization" 
                            secondary="Create, edit, and organize tiles and groups on your personal dashboard"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Theme Selection" 
                            secondary="Choose from 20+ predefined themes or create custom color schemes"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Personal Settings" 
                            secondary="Manage your account settings and preferences"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Password Changes" 
                            secondary="Change your own account password for security"
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    User Management
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Creating Users" 
                        secondary={user?.role === 'superuser' 
                          ? "Create regular users and admin accounts"
                          : "Create regular user accounts"
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Password Management" 
                        secondary="Change passwords for users you manage"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Account Status" 
                        secondary="Activate or deactivate user accounts as needed"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="User Deletion" 
                        secondary="Delete user accounts (this action cannot be undone)"
                      />
                    </ListItem>
                  </List>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Team Management
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Create Teams" 
                        secondary="Set up teams for easy group dashboard sharing"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Manage Members" 
                        secondary="Add or remove team members by email address"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Shared Access" 
                        secondary="All admins and superusers can view and manage all teams"
                      />
                    </ListItem>
                    {user?.role === 'superuser' && (
                      <ListItem>
                        <ListItemText 
                          primary="Default Configuration" 
                          secondary="Set up default dashboard templates for new users"
                        />
                      </ListItem>
                    )}
                  </List>
                </Grid>
              </Grid>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Typography variant="h5" gutterBottom>
              Keyboard Shortcuts
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Navigation
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <KeyboardShortcut keys="Ctrl+K" description="Quick search tiles" />
                  <KeyboardShortcut keys="Ctrl+E" description="Toggle edit mode" />
                  <KeyboardShortcut keys="Ctrl+H" description="Open help page" />
                  <KeyboardShortcut keys="Ctrl+T" description="Open theme selector" />
                </Box>

                <Typography variant="h6" gutterBottom>
                  Edit Mode
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <KeyboardShortcut keys="Ctrl+G" description="Add new group" />
                  <KeyboardShortcut keys="Ctrl+Shift+T" description="Add new tile" />
                  <KeyboardShortcut keys="Escape" description="Exit edit mode" />
                  <KeyboardShortcut keys="Delete" description="Delete selected item" />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Tile Actions
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <KeyboardShortcut keys="Enter" description="Open selected tile" />
                  <KeyboardShortcut keys="Ctrl+Enter" description="Open in new tab" />
                  <KeyboardShortcut keys="Right Click" description="Open tile menu" />
                  <KeyboardShortcut keys="Drag" description="Move tile or group" />
                </Box>

                <Typography variant="h6" gutterBottom>
                  General
                </Typography>
                <Box>
                  <KeyboardShortcut keys="Ctrl+S" description="Save changes" />
                  <KeyboardShortcut keys="Ctrl+R" description="Refresh dashboard" />
                  <KeyboardShortcut keys="F11" description="Toggle fullscreen" />
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <Typography variant="h5" gutterBottom>
              Troubleshooting
            </Typography>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Login Issues</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Forgot Password" 
                      secondary="Contact your administrator to reset your password"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Account Locked" 
                      secondary="Too many failed login attempts. Wait 15 minutes or contact admin"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Invalid Credentials" 
                      secondary="Check your email and password. Email addresses are case-sensitive"
                    />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Dashboard Problems</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Tiles Not Loading" 
                      secondary="Try refreshing the page (Ctrl+R) or clearing your browser cache"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Drag & Drop Not Working" 
                      secondary="Make sure you're in edit mode and using a supported browser"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Icons Missing" 
                      secondary="Icons should load automatically. Check your internet connection"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Changes Not Saving" 
                      secondary="Changes are auto-saved. If they're not persisting, check network connection"
                    />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Performance Issues</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Slow Loading" 
                      secondary="Try reducing the number of tiles or groups on your dashboard"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Browser Compatibility" 
                      secondary="Best experience with Chrome, Firefox, Safari, or Edge (latest versions)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Memory Usage" 
                      secondary="Close unused browser tabs and restart your browser periodically"
                    />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2" color="info.contrastText">
                <strong>Need more help?</strong> Contact your system administrator or IT support team for assistance 
                with account-related issues or technical problems.
              </Typography>
            </Box>
          </TabPanel>
        </Paper>
      </Container>
    </Box>
  );
};

export default HelpPage;